import React from 'react';
import useResponsive from '@/hooks/useResponsive';
import type { SubNavProps } from '@/components/custom/SubNav';

export interface UseNavigationArgs {
  activeKey?: string;
  onNavigate?: (key: string) => void;
  subNav?: SubNavProps;
}

export interface UseNavigationResult {
  // search
  isSearchOpen: boolean;
  toggleSearch: () => void;

  // hover/selection
  hoveredItem: string | null;
  setHoveredItem: React.Dispatch<React.SetStateAction<string | null>>;
  selectedItem: string | null;

  // responsive flags
  isMobile: boolean;
  isTablet: boolean;

  // subnav resolution
  shouldShowSubNav: boolean;
  resolvedVariant: SubNavProps['variant'] | undefined;
  resolvedSide: SubNavProps['side'] | undefined;
  resolvedIsOpen: boolean | undefined;
  renderedActiveTab: string;

  // handlers
  handleNavItemClick: (key: string) => void;
  handleSubNavClose: () => void;
}

export function useNavigation({ activeKey, onNavigate, subNav }: UseNavigationArgs): UseNavigationResult {
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [hoveredItem, setHoveredItem] = React.useState<string | null>(null);
  const [selectedItem, setSelectedItem] = React.useState<string | null>(null);
  const [isSubNavOpen, setIsSubNavOpen] = React.useState(false);
  const { isMobile, isTablet } = useResponsive();

  const activeSubNavKey = hoveredItem || selectedItem;

  const shouldShowSubNav = React.useMemo(() => {
    return !!(subNav?.navigationData && activeSubNavKey);
  }, [subNav?.navigationData, activeSubNavKey]);

  const resolvedVariant = React.useMemo<SubNavProps['variant'] | undefined>(() => {
    if (!subNav) return undefined;
    if (subNav.variant === 'sheet') return 'sheet';
    if (!subNav.variant) return (isMobile || isTablet) ? 'sheet' : 'card';
    return subNav.variant;
  }, [subNav, isMobile, isTablet]);

  const resolvedSide = React.useMemo<SubNavProps['side'] | undefined>(() => {
    return resolvedVariant === 'sheet'
      ? (subNav?.side || (isMobile ? 'bottom' : 'left'))
      : undefined;
  }, [resolvedVariant, subNav?.side, isMobile]);

  const resolvedIsOpen = React.useMemo<boolean | undefined>(() => {
    return resolvedVariant === 'sheet'
      ? (subNav?.isOpen !== undefined ? subNav.isOpen : isSubNavOpen)
      : undefined;
  }, [resolvedVariant, subNav?.isOpen, isSubNavOpen]);

  const renderedActiveTab = React.useMemo<string>(() => {
    if (resolvedVariant === 'sheet') {
      return selectedItem || subNav?.activeTab || '';
    }
    return activeSubNavKey || subNav?.activeTab || '';
  }, [resolvedVariant, selectedItem, activeSubNavKey, subNav?.activeTab]);

  // Keep selection in sync with activeKey AFTER initial mount to preserve default hidden state
  const hasSyncedActiveKeyRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasSyncedActiveKeyRef.current) {
      hasSyncedActiveKeyRef.current = true; // skip initial sync
      return;
    }
    if (activeKey) setSelectedItem(activeKey);
  }, [activeKey]);

  // Preserve experience when resizing: open sheet on mobile/tablet only when a section was selected (clicked)
  React.useEffect(() => {
    if (!subNav) return;
    if ((isMobile || isTablet) && selectedItem) {
      setIsSubNavOpen(true);
    }
  }, [isMobile, isTablet, selectedItem, subNav]);

  // Optional: when switching back to desktop (card), close stale sheet state
  React.useEffect(() => {
    if (resolvedVariant === 'card' && isSubNavOpen) {
      setIsSubNavOpen(false);
    }
  }, [resolvedVariant, isSubNavOpen]);

  const handleNavItemClick = (key: string) => {
    // Toggle off if clicking the same item
    if (selectedItem === key) {
      setSelectedItem(null);
      setIsSubNavOpen(false);
    } else {
      setSelectedItem(key);
      onNavigate?.(key);
      if (isTablet || isMobile) {
        setIsSubNavOpen(true);
      }
    }
  };

  const handleSubNavClose = () => {
    if (resolvedVariant === 'card') {
      setHoveredItem(null);
      setSelectedItem(null);
    } else {
      setIsSubNavOpen(false);
    }
  };

  const toggleSearch = () => setIsSearchOpen((s) => !s);

  return {
    isSearchOpen,
    toggleSearch,
    hoveredItem,
    setHoveredItem,
    selectedItem,
    isMobile,
    isTablet,
    shouldShowSubNav,
    resolvedVariant,
    resolvedSide,
    resolvedIsOpen,
    renderedActiveTab,
    handleNavItemClick,
    handleSubNavClose,
  };
}
